import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface Child {
  name: string;
  age?: number;
  grade?: string;
  special_needs: string[];
  current_support?: string[];
}

export interface LeadImportData {
  email: string;
  parent_name: string;
  phone?: string;
  timeline?: string;
  timeline_date?: string;
  biggest_concerns?: string;
  children: Child[];
}

export interface ValidationResult {
  valid: Array<{
    lineNumber: number;
    lead: LeadImportData;
  }>;
  invalid: Array<{
    lineNumber: number;
    lead: LeadImportData;
    errors: string[];
  }>;
  duplicates: Array<{
    lineNumber: number;
    email: string;
    reason: string;
  }>;
  warnings: Array<{
    lineNumber: number;
    lead: LeadImportData;
    warnings: string[];
  }>;
}

export interface ValidationResponse {
  success: boolean;
  validation: ValidationResult;
  summary: {
    total: number;
    valid: number;
    invalid: number;
    duplicates: number;
    warnings: number;
  };
}

export interface ImportResult {
  created: number;
  skipped: number;
  errors: number;
  details: {
    created: Array<{
      lineNumber: number;
      leadId: number;
      email: string;
      parent_name: string;
    }>;
    skipped: Array<{
      lineNumber: number;
      email: string;
      reason: string;
    }>;
    errors: Array<{
      lineNumber: number;
      email: string;
      error: string;
    }>;
  };
}

export interface BulkImportResponse {
  success: boolean;
  message: string;
  results: ImportResult;
}

export interface SingleLeadResponse {
  success: boolean;
  message: string;
  lead: any;
  segments: string[];
}

class LeadImportService {
  // Create a single lead manually
  async createSingleLead(leadData: LeadImportData): Promise<SingleLeadResponse> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/leads/import/single`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(leadData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create lead');
    }

    return response.json();
  }

  // Validate leads before bulk import
  async validateLeads(leads: LeadImportData[]): Promise<ValidationResponse> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/leads/import/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ leads }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to validate leads');
    }

    return response.json();
  }

  // Bulk import leads
  async bulkImportLeads(
    leads: LeadImportData[],
    skipDuplicates: boolean = true
  ): Promise<BulkImportResponse> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/leads/import/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ leads, skipDuplicates }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to import leads');
    }

    return response.json();
  }

  // Download CSV import template
  async downloadImportTemplate(): Promise<void> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/leads/import/template`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download template');
    }

    // Create blob and download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'lead-import-template.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // Parse CSV file to LeadImportData format
  parseCsvToLeads(csvText: string): LeadImportData[] {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV file must contain header row and at least one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().replace(/['"]/g, ''));
    const leads: LeadImportData[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCsvLine(lines[i]);
      if (values.length === 0) continue; // Skip empty lines

      const leadData: LeadImportData = {
        email: this.getCsvValue(headers, values, 'email') || '',
        parent_name: this.getCsvValue(headers, values, 'parent_name') || '',
        phone: this.getCsvValue(headers, values, 'phone') || undefined,
        timeline: this.getCsvValue(headers, values, 'timeline') || undefined,
        timeline_date: this.getCsvValue(headers, values, 'timeline_date') || undefined,
        biggest_concerns: this.getCsvValue(headers, values, 'biggest_concerns') || undefined,
        children: []
      };

      // Parse children data (supports up to 3 children in CSV format)
      for (let childIndex = 1; childIndex <= 3; childIndex++) {
        const childName = this.getCsvValue(headers, values, `child${childIndex}_name`);
        if (childName) {
          const child: Child = {
            name: childName,
            age: this.parseNumber(this.getCsvValue(headers, values, `child${childIndex}_age`)),
            grade: this.getCsvValue(headers, values, `child${childIndex}_grade`) || undefined,
            special_needs: this.parseArray(this.getCsvValue(headers, values, `child${childIndex}_special_needs`)),
            current_support: this.parseArray(this.getCsvValue(headers, values, `child${childIndex}_current_support`))
          };
          leadData.children.push(child);
        }
      }

      // Only add lead if it has required data
      if (leadData.email && leadData.parent_name && leadData.children.length > 0) {
        leads.push(leadData);
      }
    }

    return leads;
  }

  // Helper method to parse CSV line handling quotes and commas
  private parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  }

  // Helper method to get value from CSV by header name
  private getCsvValue(headers: string[], values: string[], headerName: string): string | undefined {
    const index = headers.findIndex(h => h.toLowerCase() === headerName.toLowerCase());
    if (index === -1 || index >= values.length) return undefined;
    const value = values[index].replace(/['"]/g, '').trim();
    return value === '' ? undefined : value;
  }

  // Helper method to parse number from string
  private parseNumber(value: string | undefined): number | undefined {
    if (!value) return undefined;
    const num = parseInt(value, 10);
    return isNaN(num) ? undefined : num;
  }

  // Helper method to parse comma-separated array
  private parseArray(value: string | undefined): string[] {
    if (!value) return [];
    return value.split(',').map(v => v.trim()).filter(v => v !== '' && v.toLowerCase() !== 'none');
  }

  // Helper method to convert LeadImportData to CSV format
  convertLeadsToCsv(leads: LeadImportData[]): string {
    const headers = [
      'email', 'parent_name', 'phone', 'timeline', 'timeline_date', 'biggest_concerns',
      'child1_name', 'child1_age', 'child1_grade', 'child1_special_needs', 'child1_current_support',
      'child2_name', 'child2_age', 'child2_grade', 'child2_special_needs', 'child2_current_support',
      'child3_name', 'child3_age', 'child3_grade', 'child3_special_needs', 'child3_current_support'
    ];

    const rows = leads.map(lead => {
      const row: string[] = [
        lead.email,
        lead.parent_name,
        lead.phone || '',
        lead.timeline || '',
        lead.timeline_date || '',
        lead.biggest_concerns || ''
      ];

      // Add up to 3 children
      for (let i = 0; i < 3; i++) {
        const child = lead.children[i];
        if (child) {
          row.push(
            child.name,
            child.age?.toString() || '',
            child.grade || '',
            child.special_needs.join(','),
            child.current_support?.join(',') || ''
          );
        } else {
          row.push('', '', '', '', '');
        }
      }

      return row.map(value => {
        // Wrap in quotes if contains comma or quote
        if (value.includes(',') || value.includes('"')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',');
    });

    return [headers.join(','), ...rows].join('\n');
  }
}

export const leadImportService = new LeadImportService();
export default leadImportService;